import { formatCurrencyAmount } from "@goodfill/format-number";
import { ChartConfiguration } from "@goodfill/ui-components";
import { DebtDistribution, debtDistributionRatingLabel, ratingRangeIds } from "./DeptDistribution";
import { calculateRandomColorFromSeed } from "@goodfill/color";

export const createDeptDistributionBarChartConfiguration = (
  debtDistribution: DebtDistribution,
): ChartConfiguration => {
  const maxMaturityYears = debtDistribution.reduce((max, { maturityYears }) => Math.max(max, maturityYears), 0);
  const datasets = ratingRangeIds.map((ratingRangeId) => ({
    ratingRangeId,
    // initialize data with as many zeros as max maturityYears found
    data: Array.from({ length: maxMaturityYears }).fill(0) as number[],
  }));

  debtDistribution.forEach(({ ratingRangeId, maturityYears, faceAmount }) => {
    const ratingRangeIndex = datasets.findIndex((dataset) => dataset.ratingRangeId === ratingRangeId);

    const maturityIndex = maturityYears - 1;
    if (ratingRangeIndex >= 0) {
      datasets[ratingRangeIndex].data[maturityIndex] += faceAmount.div(1000).toNumber();
    }
  });

  const chartDatasets = datasets.map(({ ratingRangeId, data }) => ({
    label: debtDistributionRatingLabel[ratingRangeId],
    backgroundColor: calculateRandomColorFromSeed(ratingRangeId),
    data,
  }));

  const barConfigurationLabels: number[] = [];
  for (let i = 1; i <= maxMaturityYears; i++) {
    barConfigurationLabels.push(i);
  }

  return {
    type: "bar",
    data: {
      labels: barConfigurationLabels,
      datasets: chartDatasets,
    },
    options: {
      scales: {
        x: {
          grid: {
            // borderDash: [5, 5],
            display: true,
          },
          stacked: true,
          title: {
            display: true,
            text: "$1000 Face amount",
            font: {
              size: 14,
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          stacked: true,
          title: {
            display: true,
            text: "Maturity (years)",
            font: {
              size: 14,
            },
          },
        },
      },
      indexAxis: "y",
      plugins: {
        tooltip: {
          callbacks: {
            afterLabel: (context) => {
              const maturity = context.label;
              return `Maturity (years): ${maturity}`;
            },
            label: (context) => {
              const rating = context.dataset.label ?? "";
              return rating;
            },
            title: (context) => {
              const value = Number(context[0].raw) * 1000;
              const amount = formatCurrencyAmount({ value, numDecimals: 0 });
              return `Face amount: ${amount}`;
            },
          },
          padding: 12,
        },
      },
    },
  };
};
